import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation, Trans } from "react-i18next";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { ArrowRight, Coffee, TrendingUp, QrCode, Star, Gift, MessageSquare, Sparkles } from "lucide-react";

export const Route = createFileRoute("/why-tack")({
  head: () => ({
    meta: [
      { title: "Tack. Därför kommer kunderna tillbaka" },
      { name: "description", content: "Småföretag tappar kunder för att de glömmer bort dem, inte till konkurrenter. Tack är en QR-kod på 5 sekunder som gör förstagångsbesökare till stammisar. Ingen app, inget stämpelkort." },
      { property: "og:title", content: "Tack. Därför kommer kunderna tillbaka" },
      { property: "og:description", content: "En QR-kod på 5 sekunder som får kunderna att komma tillbaka. Ingen app, inget stämpelkort." },
      { property: "og:url", content: "https://tack.company/why-tack" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/why-tack" }],
  }),
  component: WhyTackPage,
});

function WhyTackPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" aria-label="Tack home" className="hover:opacity-80 transition-opacity">
            <TackLogo size="md" showSlogan />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/10 hover:text-primary">{t("whyTack.nav.home")}</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm">{t("whyTack.nav.startFree")}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary mb-3 tracking-wide uppercase">{t("whyTack.hero.eyebrow")}</p>
          <h1 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">
            {t("whyTack.hero.title")}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            {t("whyTack.hero.subtitle")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup">
              <Button size="lg" className="gap-2">{t("whyTack.hero.ctaPrimary")} <ArrowRight size={16} /></Button>
            </Link>
            <Link to="/demo">
              <Button variant="outline" size="lg">{t("whyTack.hero.ctaSecondary")}</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="px-4 pb-16">
        <div className="max-w-2xl mx-auto space-y-16">

          {/* Problem */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Coffee size={20} className="text-primary" />
              </div>
              <h2 className="text-xl font-display font-bold">{t("whyTack.problem.title")}</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{t("whyTack.problem.p1")}</p>
              <p>{t("whyTack.problem.p2")}</p>
            </div>
          </div>

          {/* The math */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <TrendingUp size={20} className="text-primary" />
              </div>
              <h2 className="text-xl font-display font-bold">{t("whyTack.math.title")}</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                <Trans i18nKey="whyTack.math.p1" components={{ strong: <strong className="text-foreground" /> }} />
              </p>
              <p>{t("whyTack.math.p2")}</p>
            </div>
          </div>

          {/* How it works */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <QrCode size={20} className="text-primary" />
              </div>
              <h2 className="text-xl font-display font-bold">{t("whyTack.how.title")}</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-sm"><QrCode size={16} /> {t("whyTack.how.scan.label")}</div>
                <p className="text-sm text-muted-foreground">{t("whyTack.how.scan.desc")}</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-sm"><Star size={16} /> {t("whyTack.how.rate.label")}</div>
                <p className="text-sm text-muted-foreground">{t("whyTack.how.rate.desc")}</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-sm"><Gift size={16} /> {t("whyTack.how.return.label")}</div>
                <p className="text-sm text-muted-foreground">{t("whyTack.how.return.desc")}</p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <MessageSquare size={20} className="text-primary" />
              </div>
              <h2 className="text-xl font-display font-bold">{t("whyTack.features.title")}</h2>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
              {(["sms","nudge","flash","cashier","dashboard","brand"] as const).map((k) => (
                <li key={k} className="rounded-lg border border-border p-3">
                  <strong className="text-foreground">{t(`whyTack.features.${k}.name`)}</strong> — {t(`whyTack.features.${k}.desc`)}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-8 text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary mb-3">
              <Sparkles size={12} /> {t("whyTack.cta.badge")}
            </div>
            <h2 className="text-2xl font-display font-bold mb-2">{t("whyTack.cta.title")}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("whyTack.cta.desc")}</p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">{t("whyTack.cta.action")} <ArrowRight size={16} /></Button>
            </Link>
          </div>

        </div>
      </section>

      <SiteFooter variant="marketing" />
    </div>
  );
}
